import React from "react";
import PropTypes from "prop-types";
import { Menu } from "./Menu";
import { Wrapper } from "./Wrapper";
import { useIntl } from "react-intl";

export function MenuList(props) {
  const intl = useIntl();
  const onSelect = (sectionValue) => () => props.onSelect(sectionValue);

  // Ghost/highlight only when enabled (hotspot)
  const ghostEnabled = !!props.enableActiveGhost; // default false
  const ghostRef = React.useRef(null);
  const wrapperRef = React.useRef(null);
  const activeMenuRef = React.useRef(null);
  const [ghostStyle, setGhostStyle] = React.useState({ opacity: 0 });

  const getClassName = ({ current, locked }) => {
    return current
      ? props.activeMenuClass
      : locked === true
        ? props.lockedMenuClass
        : props.inactiveMenuClass;
  };

  const getTabIndex = (menu) => {
    if (menu.locked) return -1;
    return 0;
  };

  const handleScrollIntoView = () => {
    const el = activeMenuRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  const groupChapters = (chapters) => {
    const result = [];
    const experimentGroups = {};

    chapters.forEach((chapter) => {
      if (chapter.visible === true || chapter.visible == null) {
        if (chapter.chapterGroup) {
          if (!experimentGroups[chapter.chapterGroup]) {
            experimentGroups[chapter.chapterGroup] = [];
            result.push(experimentGroups[chapter.chapterGroup]);
          }
          experimentGroups[chapter.chapterGroup].push(chapter);
        } else {
          result.push(chapter);
        }
      }
    });

    return result;
  };
  const groupedMenuList = groupChapters(props.menuList);

  // Find the active menu DOM node and update ghost position/size
  React.useLayoutEffect(() => {
    if (!ghostEnabled) return;
    const wrapper = wrapperRef.current;
    const active = activeMenuRef.current;
    if (!wrapper) return;
    if (active) {
      const rect = active.getBoundingClientRect();
      const parentRect = wrapper.getBoundingClientRect();
      setGhostStyle({
        opacity: 1,
        position: "absolute",
        pointerEvents: "none",
        background: "#faac48",
        borderRadius: "8px",
        zIndex: 0,
        left: rect.left - parentRect.left + wrapper.scrollLeft + "px",
        top: rect.top - parentRect.top + wrapper.scrollTop + "px",
        width: rect.width + "px",
        height: rect.height + "px",
        transition: "all 300ms cubic-bezier(0.4,0,0.2,1)",
      });
    } else {
      setGhostStyle({ opacity: 0 });
    }
  }, [ghostEnabled, props.menuList.map((m) => m.current).join(",")]);

  React.useLayoutEffect(() => {
    if (!ghostEnabled) return;
    handleScrollIntoView();
  }, [ghostEnabled, props.menuList.map((m) => m.current).join(",")]);

  const renderMenu = (menu) => {
    if (Array.isArray(menu)) {
      return (
        <div className="chapter_group">
          <div className="chapter_group_heading">
            {intl.formatMessage({
              id: menu[0].chapterGroup,
              defaultMessage: menu[0].chapterGroup,
              description: "",
            })}
          </div>
          {menu.map(renderMenu)}
        </div>
      );
    } else {
      const ref = menu.current ? activeMenuRef : undefined;
      return (
        <Menu
          tabIndex={getTabIndex(menu)}
          aria-disabled={menu.locked || undefined}
          displayProgress={props.displayProgress}
          {...menu}
          key={menu.value}
          className={getClassName(menu)}
          onSelect={!menu.locked ? onSelect(menu.value) : undefined}
          menuList={props.menuList}
          ref={ref}
        />
      );
    }
  };

  return (
    <Wrapper className={props.className}>
      {ghostEnabled ? (
        <div
          ref={wrapperRef}
          className={props.listContainerClass}
          style={{ position: "relative" }}
        >
          <div ref={ghostRef} style={ghostStyle} aria-hidden="true" />
          {groupedMenuList.map(renderMenu)}
        </div>
      ) : (
        groupedMenuList.map(renderMenu)
      )}
    </Wrapper>
  );
}

MenuList.propTypes = {
  /**
   * Callback fired when a Menu is Selected/Clicked
   */
  onSelect: PropTypes.func,
  /**
   * List of Menu to display
   */
  menuList: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Title to display in  menu
       */
      title: PropTypes.string.isRequired,
      /**
       * Boolean specifying if menu is selected
       */
      current: PropTypes.bool.isRequired,
      /**
       * Index number of menu
       */
      number: PropTypes.number.isRequired,
      /**
       * Unique string value of the menu
       */
      value: PropTypes.string.isRequired,
      /**
       * Boolean set when section is visited
       */
      visited: PropTypes.bool.isRequired,
    })
  ),
  /**
   * ClassName to set on InactiveMenu
   */
  inactiveMenuClass: PropTypes.string,
  /**
   * ClassName to set on ActiveMenu
   */
  activeMenuClass: PropTypes.string,
  /**
   * ClassName to set on LockedMenu Section
   */
  lockedMenuClass: PropTypes.string,
};

MenuList.defaultProps = {
  inactiveMenuClass: "",
  activeMenuClass: "",
};
